import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { pmsFrom } from '@/integrations/pms';

const SESSION_KEY = 'lead_tracking_session';
const AI_SOURCE_KEY = 'lead_ai_source';

// Known AI search engine referrer patterns
const AI_REFERRER_PATTERNS: { pattern: RegExp; source: string }[] = [
  // OpenAI / ChatGPT
  { pattern: /chat\.openai\.com|chatgpt\.com|searchgpt/i, source: 'ChatGPT' },
  // Microsoft Copilot / Bing Chat
  { pattern: /copilot\.microsoft\.com|bing\.com\/chat|bing\.com\/search.*&showconv/i, source: 'Microsoft Copilot' },
  // Google Gemini / Bard / AI Overviews
  { pattern: /gemini\.google\.com|bard\.google\.com/i, source: 'Google Gemini' },
  { pattern: /google\.com\/search.*#ip=1|google\.com\/.*udm=14/i, source: 'Google AI Overview' },
  // Perplexity
  { pattern: /perplexity\.ai/i, source: 'Perplexity' },
  // Anthropic / Claude
  { pattern: /claude\.ai|anthropic\.com/i, source: 'Claude' },
  // xAI / Grok
  { pattern: /grok\.x\.ai|x\.com\/i\/grok|grok\.com/i, source: 'Grok' },
  // Meta AI
  { pattern: /meta\.ai|ai\.meta\.com/i, source: 'Meta AI' },
  // You.com
  { pattern: /you\.com/i, source: 'You.com' },
  // Phind
  { pattern: /phind\.com/i, source: 'Phind' },
  // Kagi
  { pattern: /kagi\.com/i, source: 'Kagi' },
  // Cohere
  { pattern: /cohere\.com|coral\.cohere/i, source: 'Cohere' },
  // Mistral / Le Chat
  { pattern: /chat\.mistral\.ai|mistral\.ai/i, source: 'Mistral' },
  // DeepSeek
  { pattern: /chat\.deepseek\.com|deepseek\.com/i, source: 'DeepSeek' },
  // Jasper
  { pattern: /jasper\.ai/i, source: 'Jasper' },
  // HuggingChat
  { pattern: /huggingface\.co\/chat/i, source: 'HuggingChat' },
  // Pi / Inflection
  { pattern: /pi\.ai|inflection\.ai/i, source: 'Pi' },
  // Brave Leo
  { pattern: /search\.brave\.com/i, source: 'Brave Leo' },
  // DuckDuckGo AI
  { pattern: /duckduckgo\.com\/\?.*ia=chat/i, source: 'DuckDuckGo AI' },
  // Arc Search
  { pattern: /arc\.net/i, source: 'Arc Search' },
];

// UTM-based AI source detection
const UTM_AI_SOURCES: Record<string, string> = {
  'chatgpt': 'ChatGPT',
  'openai': 'ChatGPT',
  'searchgpt': 'ChatGPT',
  'perplexity': 'Perplexity',
  'gemini': 'Google Gemini',
  'bard': 'Google Gemini',
  'google-ai': 'Google AI Overview',
  'ai-overview': 'Google AI Overview',
  'ai-overviews': 'Google AI Overview',
  'sge': 'Google AI Overview',
  'copilot': 'Microsoft Copilot',
  'bing-chat': 'Microsoft Copilot',
  'claude': 'Claude',
  'anthropic': 'Claude',
  'grok': 'Grok',
  'xai': 'Grok',
  'meta-ai': 'Meta AI',
  'you-com': 'You.com',
  'phind': 'Phind',
  'kagi': 'Kagi',
  'cohere': 'Cohere',
  'mistral': 'Mistral',
  'deepseek': 'DeepSeek',
  'jasper': 'Jasper',
  'huggingchat': 'HuggingChat',
  'pi-ai': 'Pi',
  'brave-leo': 'Brave Leo',
  'brave': 'Brave Leo',
  'duckduckgo-ai': 'DuckDuckGo AI',
  'arc-search': 'Arc Search',
};

/**
 * Detect if the visitor arrived from an AI search engine.
 * Checks referrer URL and UTM parameters.
 */
function detectAISource(): string | null {
  // Already detected this session?
  const cached = sessionStorage.getItem(AI_SOURCE_KEY);
  if (cached) return cached;

  // 1. Check referrer
  const referrer = document.referrer;
  if (referrer) {
    for (const { pattern, source } of AI_REFERRER_PATTERNS) {
      if (pattern.test(referrer)) {
        sessionStorage.setItem(AI_SOURCE_KEY, source);
        return source;
      }
    }
  }

  // 2. Check UTM parameters: utm_source, utm_medium, utm_campaign
  const params = new URLSearchParams(window.location.search);
  const utmSource = (params.get('utm_source') || '').toLowerCase();
  const utmMedium = (params.get('utm_medium') || '').toLowerCase();
  const utmCampaign = (params.get('utm_campaign') || '').toLowerCase();

  // Check utm_source directly
  if (utmSource && UTM_AI_SOURCES[utmSource]) {
    const source = UTM_AI_SOURCES[utmSource];
    sessionStorage.setItem(AI_SOURCE_KEY, source);
    return source;
  }

  // Check if utm_medium mentions AI
  if (utmMedium === 'ai-search' || utmMedium === 'ai_search' || utmMedium === 'ai-referral') {
    const source = UTM_AI_SOURCES[utmSource] || `AI Search (${utmSource || 'unknown'})`;
    sessionStorage.setItem(AI_SOURCE_KEY, source);
    return source;
  }

  // Check if campaign contains AI platform names
  for (const [key, source] of Object.entries(UTM_AI_SOURCES)) {
    if (utmCampaign.includes(key) || utmSource.includes(key)) {
      sessionStorage.setItem(AI_SOURCE_KEY, source);
      return source;
    }
  }

  return null;
}

function getOrCreateSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

export function usePageTracking() {
  const location = useLocation();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    // Only track public pages, not admin
    if (location.pathname.startsWith('/admin')) return;
    
    // Avoid duplicate tracking for same path
    if (lastTrackedPath.current === location.pathname) return;
    lastTrackedPath.current = location.pathname;

    const trackPageView = async () => {
      const sessionId = getOrCreateSessionId();
      const aiSource = detectAISource();
      
      try {
        await pmsFrom('lead_interactions').insert({
          session_id: sessionId,
          page_path: location.pathname,
          page_title: document.title,
          interaction_type: 'page_view',
          metadata: {
            referrer: document.referrer,
            search: location.search,
            ...(aiSource ? { ai_source: aiSource } : {}),
          },
        });
      } catch (error) {
        // Silently fail - don't disrupt user experience
        console.debug('Page tracking error:', error);
      }
    };

    // Small delay to ensure page has loaded
    const timeout = setTimeout(trackPageView, 500);
    return () => clearTimeout(timeout);
  }, [location.pathname]);
}

export function getSessionId(): string {
  return getOrCreateSessionId();
}

/**
 * Get the detected AI source for this session, if any.
 */
export function getAISource(): string | null {
  return sessionStorage.getItem(AI_SOURCE_KEY);
}

// Link a session to a lead when they submit a form
export async function linkSessionToLead(leadId: string): Promise<void> {
  const sessionId = getOrCreateSessionId();
  const aiSource = getAISource();
  
  try {
    // Update all interactions from this session to link to the lead
    await pmsFrom('lead_interactions')
      .update({ lead_id: leadId })
      .eq('session_id', sessionId);

    // If we detected an AI source, tag the lead record
    if (aiSource) {
      await pmsFrom('leads')
        .update({ source: `ai:${aiSource}` })
        .eq('id', leadId);
    }
  } catch (error) {
    console.debug('Session linking error:', error);
  }
}
